
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createTask } from "../api/createTask";
import { showToast } from "../../../utils/toast";
import { QUERY_KEYS } from "service/querykeys";

export const useCreateTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTask,

    onMutate: async (newTaskParams) => {
      const { projectUuid } = newTaskParams;
      const tempId = `temp-${Date.now()}`;

      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.TASKS(projectUuid) });
      await queryClient.cancelQueries({ queryKey: ["tasks", "infinite", projectUuid] });

      // Snapshot the previous values
      const previousTasks = queryClient.getQueryData(QUERY_KEYS.TASKS(projectUuid));
      const previousInfiniteTasks = queryClient.getQueriesData({
        queryKey: ["tasks", "infinite", projectUuid],
      });

      const isUpdate = !!(newTaskParams as any).workItemUuid;

      // Helper to update a task in a list — walks the tree recursively to handle child items
      const updateTaskInList = (list: any[]): any[] => {
        if (!Array.isArray(list)) return list;
        return list.map((t: any) => {
          // Update this item if it matches
          if (t.workItemUuid === (newTaskParams as any).workItemUuid) {
            return {
              ...t,
              ...newTaskParams,
              title:
                (newTaskParams as any).workItemTitle ||
                (newTaskParams as any).title ||
                t.title,
            };
          }
          // Recursively update children
          const updatedChildren = t.childWorkItems
            ? updateTaskInList(t.childWorkItems)
            : t.childWorkItems;
          const updatedDTOList = t.workItemDTOList
            ? updateTaskInList(t.workItemDTOList)
            : t.workItemDTOList;

          if (updatedChildren !== t.childWorkItems || updatedDTOList !== t.workItemDTOList) {
            return { ...t, childWorkItems: updatedChildren, workItemDTOList: updatedDTOList };
          }
          return t;
        });
      };

      // Helper to add a task to a list
      const addTaskToList = (list: any[]) => {
        if (!Array.isArray(list)) return list;
        const optimisticTask = {
          workItemUuid: tempId,
          workItemKey: "SAVING...", // Placeholder for immediate feedback
          ...newTaskParams,
          title: (newTaskParams as any).workItemTitle || "New Task",
          createdDate: new Date().toISOString(),
          // the UI expects these fields, ensure they exist
          workStatusUuid: (newTaskParams as any).workStatusUuid,
          workPriority: (newTaskParams as any).workPriority || "MEDIUM",
          assignee: null,
          reporter: null,
        };
        // Prepend the optimistic task to the start (newest first)
        return [optimisticTask, ...list];
      };

      // 1. Optimistically update the standard (non-infinite) cache if it exists
      queryClient.setQueryData(QUERY_KEYS.TASKS(projectUuid), (old: any) => {
        if (!old) return old;
        return isUpdate ? updateTaskInList(old) : addTaskToList(old);
      });

      // 2. Optimistically update all matching infinite queries (they might have different filters)
      queryClient.setQueriesData(
        { queryKey: ["tasks", "infinite", projectUuid] },
        (old: any) => {
          if (!old || !old.pages) return old;

          const newPages = old.pages.map((page: any, index: number) => {
            if (!page.response?.[0]?.data) return page;

            let newData = page.response[0].data;
            if (isUpdate) {
              newData = updateTaskInList(newData);
            } else if (index === 0) {
              // Only prepend to the first page for new tasks
              newData = addTaskToList(newData);
            }

            // Create a new response object with the updated data
            const newResponse = [...page.response];
            newResponse[0] = { ...newResponse[0], data: newData };

            return { ...page, response: newResponse };
          });

          return { ...old, pages: newPages };
        }
      );

      // Return a context object with the snapshotted values for rollback
      return { previousTasks, previousInfiniteTasks, projectUuid, tempId };
    },

    onError: (error: any, _variables, context) => {
      // Rollback standard cache
      if (context?.previousTasks) {
        queryClient.setQueryData(
          QUERY_KEYS.TASKS(context.projectUuid),
          context.previousTasks
        );
      }

      // Rollback infinite caches
      if (context?.previousInfiniteTasks) {
        context.previousInfiniteTasks.forEach(([queryKey, data]: any) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      if (error.isBusinessError) {
        showToast.error(error.message); // backend business error
        return;
      }

      if (error.isNetworkError) {
        showToast.error("Network error. Please try again.");
        return;
      }

      showToast.error("Something went wrong");
    },

    onSettled: (_data, _error, variables: any) => {
      const projectUuid = variables.projectUuid;

      // Always refetch after error or success to ensure strict synchronization
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TASKS(projectUuid) });
      queryClient.invalidateQueries({
        queryKey: ["tasks", "infinite", projectUuid],
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TASKS() }); // Broad invalidate for safety
      
      // BROAD INVALIDATION: Refresh all task details in the project to catch hierarchy/link updates
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TASK(projectUuid) });
      queryClient.invalidateQueries({ queryKey: ['task-children', projectUuid], exact: false });

      if (variables.workItemUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TASK(projectUuid, variables.workItemUuid),
        });
      }

      // If this is a subtask, also invalidate its parent so the parent's detail view refreshes
      const parentUuid = variables.parentWorkItemUuid || variables.workItemParentWorkItemUuid;
      if (parentUuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.TASK(projectUuid, parentUuid),
        });
        
        // IMPORTANT: Also invalidate the infinite children query so parent rows in list view update
        queryClient.invalidateQueries({ 
          queryKey: ['task-children-infinite', projectUuid, parentUuid],
          exact: false 
        });
      }
    },

    onSuccess: (data, variables, context) => {
      // Reconcile the optimistic task with the real ID from the server
      if (context?.tempId && data.workItemUuid) {
        const { projectUuid } = variables;
        
        // Update standard query
        queryClient.setQueryData(QUERY_KEYS.TASKS(projectUuid), (old: any) => {
          if (!Array.isArray(old)) return old;
          return old.map(t => t.workItemUuid === context.tempId ? { ...t, workItemUuid: data.workItemUuid } : t);
        });

        // Update infinite queries
        queryClient.setQueriesData(
          { queryKey: ["tasks", "infinite", projectUuid] },
          (old: any) => {
            if (!old || !old.pages) return old;
            const newPages = old.pages.map((page: any) => {
              if (!page.response?.[0]?.data) return page;
              const newData = page.response[0].data.map((t: any) => 
                t.workItemUuid === context.tempId ? { ...t, workItemUuid: data.workItemUuid } : t
              );
              const newResponse = [...page.response];
              newResponse[0] = { ...newResponse[0], data: newData };
              return { ...page, response: newResponse };
            });
            return { ...old, pages: newPages };
          }
        );
      }
    },
  });
};
